import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, Send, X, Loader2 } from "lucide-react";
import api from "../../lib/apiClient";
import { toast } from "sonner";
import { ProHeader, StatusBadge, money, fmtDate, Loading, Empty } from "./proUi";

// Demandes du Marché : le pro parcourt les demandes ouvertes et envoie une proposition.
export default function ProDemandes() {
  const [reqs, setReqs] = useState(null);
  const [target, setTarget] = useState(null);
  const nav = useNavigate();

  const load = () => api.get("/marketplace/requests").then((r) => setReqs(r.data.requests)).catch(() => setReqs([]));
  useEffect(() => { load(); }, []);

  if (reqs === null) return <Loading />;
  return (
    <div className="space-y-6" data-testid="pro-demandes-page">
      <ProHeader eyebrow="Marché Cehnura" title="Demandes disponibles"
        subtitle="Réponds aux projets publiés par les membres en envoyant une proposition." />
      {reqs.length === 0 ? (
        <Empty icon={Inbox} title="Aucune demande ouverte pour l'instant" hint="Reviens plus tard : de nouveaux projets sont publiés régulièrement." testid="pro-demandes-empty" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {reqs.map((r) => (
            <div key={r.id} className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5 flex flex-col" data-testid={`demande-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-bold text-zinc-100">{r.title}</h3>
                <StatusBadge status={r.status} />
              </div>
              <p className="text-xs text-zinc-500 mt-0.5">Par {r.owner_name} · {fmtDate(r.created_at)}</p>
              {r.description && <p className="text-sm text-zinc-400 mt-2 line-clamp-3">{r.description}</p>}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {r.category && <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">{r.category}</span>}
                {r.budget != null && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30">Budget {money(r.budget)}</span>}
                <span className="text-[11px] text-zinc-500">{r.proposals_count} proposition(s)</span>
              </div>
              <div className="flex gap-3 mt-4 pt-3 border-t border-zinc-800/70">
                <button onClick={() => nav(`/marketplace/requests/${r.id}`)} data-testid={`demande-view-${r.id}`} className="text-sm text-zinc-400 hover:text-zinc-200">Voir le détail</button>
                {!r.is_owner && r.status === "OPEN" && (
                  <button onClick={() => setTarget(r)} data-testid={`demande-propose-${r.id}`} className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-amber-400"><Send className="w-4 h-4" />Faire une proposition</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {target && <ProposeModal req={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); toast.success("Proposition envoyée"); load(); }} />}
    </div>
  );
}

function ProposeModal({ req, onClose, onDone }) {
  const [f, setF] = useState({ message: "", price: "", delay: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.message.trim()) { toast.error("Ajoute un message"); return; }
    setBusy(true);
    try {
      await api.post(`/marketplace/requests/${req.id}/proposals`, {
        message: f.message, price: f.price === "" ? null : Number(f.price), delay: f.delay || null,
      });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="propose-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold">Proposition — {req.title}</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={4} placeholder="Présente ton approche, ton expérience…" data-testid="propose-message" className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm resize-none outline-none focus:border-amber-500" />
          <div className="grid grid-cols-2 gap-3">
            <input value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} type="number" placeholder="Tarif € (optionnel)" data-testid="propose-price" className="h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-500" />
            <input value={f.delay} onChange={(e) => setF({ ...f, delay: e.target.value })} placeholder="Délai (ex : 5 j)" data-testid="propose-delay" className="h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-500" />
          </div>
          <button onClick={submit} disabled={busy} data-testid="propose-submit" className="w-full h-12 rounded-2xl gold-gradient text-black font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}Envoyer ma proposition
          </button>
        </div>
      </div>
    </div>
  );
}
